import { inject, Pipe, PipeTransform } from '@angular/core';
import {
  PersonNounForm,
  PersonNounService,
} from '../infra/person-noun.service';

/**
 * How the pipe returns the noun:
 *  - `'text'` (default): the resolved word, e.g. `Alunos`.
 *  - `'key'`: the i18n key, e.g. `PERSON_TREATMENT.STUDENT.PLURAL`, for
 *    consumers that translate the value themselves (e.g. data-driven menus
 *    whose renderer already applies `| translate`).
 */
export type PersonNounMode = 'text' | 'key';

/**
 * Renders the tenant's person noun in templates.
 *
 * Usage:
 *   {{ 'plural' | personNoun }}         -> Clientes / Alunos / Pacientes / Membros
 *   {{ 'singular' | personNoun }}       -> Cliente  / Aluno  / Paciente  / Membro
 *   {{ 'plural' | personNoun : 'key' }} -> PERSON_TREATMENT.<SEGMENT>.PLURAL
 *
 * Impure so it re-resolves when the language or the PersonTreatment setting changes.
 */
@Pipe({
  name: 'personNoun',
  standalone: true,
  pure: false,
})
export class PersonNounPipe implements PipeTransform {
  private readonly personNounService = inject(PersonNounService);

  public transform(
    form: PersonNounForm = 'singular',
    mode: PersonNounMode = 'text'
  ): string {
    return mode === 'key'
      ? this.personNounService.key(form)
      : this.personNounService.noun(form);
  }
}
